import React, { useEffect, useMemo, useState } from "react";
import Main from "../../layouts/Main";
import { useNavigate, useParams } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import {
    Box,
    Button,
    Chip,
    Divider,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import ShuffleRoundedIcon from "@mui/icons-material/ShuffleRounded";

import Container from "../../components/Container";
import OfferImages from "../../components/presentationalComponents/OfferImages";
import CompoundsListing from "../../components/presentationalComponents/CompoundsListing";

import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
    fetchAllRegions,
    fetchCompoundsOfRegion,
    setRegionById,
} from "../../store/Vanlifer/regionSlice";
import { setSearchFormData } from "../../store/Vanlifer/vanliferComponentsSlice";
import { Compound } from "../../services/restserver-openapi";

type SortMode = "RECOMMENDED" | "AZ" | "RANDOM";

function mulberry32(seed: number) {
    return function () {
        let t = (seed += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function seededShuffle<T>(items: T[], seed: number): T[] {
    const rand = mulberry32(seed);
    const arr = [...items];
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

// robust: Suchtext aus “wahrscheinlichen” Feldern ziehen ohne deine Types zu sprengen
const compoundSearchText = (c: Compound) => {
    const anyC = c as any;
    const parts = [
        anyC.name,
        anyC.title,
        anyC.description,
        anyC.city,
        anyC.country,
        anyC.owner,
    ]
        .filter(Boolean)
        .map((x: string) => String(x));
    return parts.join(" ").toLowerCase();
};

export default function RegionSinglePage() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { regionId } = useParams();

    const region = useAppSelector((state) => state.region.region);
    const [compoundsRaw, setCompoundsRaw] = useState<Compound[]>([]);

    // Sidebar filters
    const [query, setQuery] = useState("");
    const [onlyWithImages, setOnlyWithImages] = useState(false);
    const [sortMode, setSortMode] = useState<SortMode>("RECOMMENDED");
    const [shuffleSeed, setShuffleSeed] = useState(1);

    useEffect(() => {
        dispatch(fetchAllRegions()).then(() => {
            dispatch(setRegionById(Number(regionId)));

            dispatch(fetchCompoundsOfRegion(Number(regionId)))
                .then(unwrapResult)
                .then((fetchedCompounds) => {
                    const visible = fetchedCompounds.filter((c) => c.visibleCompound);
                    setCompoundsRaw(visible);
                });
        });
    }, [dispatch, regionId]);

    const normalizedQuery = query.trim().toLowerCase();

    const filteredCompounds = useMemo(() => {
        let out = [...(compoundsRaw ?? [])];

        // Filter: images
        if (onlyWithImages) {
            out = out.filter((c) => (c.imageIds?.length ?? 0) > 0);
        }

        // Filter: query (optional)
        if (normalizedQuery) {
            out = out.filter((c) => compoundSearchText(c).includes(normalizedQuery));
        }

        // Sort
        const byName = (a: Compound, b: Compound) => {
            const an = String(((a as any).name ?? (a as any).title ?? "")).toLowerCase();
            const bn = String(((b as any).name ?? (b as any).title ?? "")).toLowerCase();
            return an.localeCompare(bn, "de", { sensitivity: "base" });
        };

        const byRecommended = (a: Compound, b: Compound) => {
            const aHas = (a.imageIds?.length ?? 0) > 0;
            const bHas = (b.imageIds?.length ?? 0) > 0;
            if (aHas && !bHas) return -1;
            if (bHas && !aHas) return 1;
            return byName(a, b);
        };

        if (sortMode === "RECOMMENDED") out.sort(byRecommended);
        if (sortMode === "AZ") out.sort(byName);
        if (sortMode === "RANDOM") out = seededShuffle(out, shuffleSeed);

        return out;
    }, [compoundsRaw, normalizedQuery, onlyWithImages, sortMode, shuffleSeed]);

    const activeChips = useMemo(() => {
        const chips: Array<{ key: string; label: string; onDelete: () => void }> = [];
        if (onlyWithImages) chips.push({ key: "img", label: "Mit Bildern", onDelete: () => setOnlyWithImages(false) });
        if (normalizedQuery) chips.push({ key: "q", label: `Suche: “${query.trim()}”`, onDelete: () => setQuery("") });
        return chips;
    }, [onlyWithImages, normalizedQuery, query]);

    function generateTourWithRegion() {
        dispatch(setSearchFormData({ startDate: null, endDate: null, regionId: Number(regionId), curationId: 0 }));
        navigate("/vanlifer/TravelPlanning");
    }

    const title = region?.name ?? "Reiseregion";
    const listingTitle = `Gastgeber in „${region?.name ?? ""}“`;

    return (
        <Main>
            <Container>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {/* Top bar */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                        <Button
                            variant="text"
                            startIcon={<ArrowBackRoundedIcon />}
                            onClick={() => navigate(-1)}
                            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
                        >
                            Zurück
                        </Button>

                        <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary", textTransform: "uppercase" }}>
                            Reiseregion
                        </Typography>
                    </Box>

                    {/* Hero (einfach & lesbar) */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            p: 2,
                            border: 0,
                            boxShadow: "none",
                            bgcolor: "background.paper",
                        }}
                    >
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr" },
                                gap: 2,
                                alignItems: "start",
                            }}
                        >
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                                    {title}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {region?.description ?? "—"}
                                </Typography>

                                <Stack direction="row" spacing={1} sx={{ mt: 2 }} useFlexGap flexWrap="wrap">
                                    <Button
                                        variant="contained"
                                        onClick={generateTourWithRegion}
                                        startIcon={<AutoAwesomeRoundedIcon />}
                                        sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
                                    >
                                        Tour in dieser Region generieren
                                    </Button>

                                    <Chip
                                        label={`${filteredCompounds.length} Gastgeber`}
                                        sx={{ fontWeight: 900 }}
                                        color="primary"
                                        variant="outlined"
                                    />
                                </Stack>
                            </Box>

                            <Box>
                                <OfferImages
                                    imageIds={region?.imageIds || []}
                                    altText={`Bild von der Region ${region?.name ?? ""}`}
                                    showLargeImage={true}
                                />
                            </Box>
                        </Box>
                    </Paper>

                    {/* Filters + Listing */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
                            gap: 2,
                            alignItems: "start",
                        }}
                    >
                        {/* Sidebar */}
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 4,
                                p: 2,
                                border: 0,
                                boxShadow: "none",
                                bgcolor: "background.paper",
                                position: { md: "sticky" },
                                top: { md: 16 },
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                                Gastgeber filtern
                            </Typography>

                            <Box sx={{ mt: 1.25 }}>
                                <TextField
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Gastgeber suchen…"
                                    size="small"
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchRoundedIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>

                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                                <Chip
                                    icon={<PhotoLibraryRoundedIcon />}
                                    label="Mit Bildern"
                                    clickable
                                    color={onlyWithImages ? "primary" : "default"}
                                    variant={onlyWithImages ? "filled" : "outlined"}
                                    onClick={() => setOnlyWithImages((v) => !v)}
                                    sx={{ fontWeight: 800 }}
                                />
                            </Stack>

                            <Divider sx={{ my: 1.5 }} />

                            <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                                Sortierung
                            </Typography>

                            <Select
                                size="small"
                                fullWidth
                                value={sortMode}
                                onChange={(e) => setSortMode(e.target.value as SortMode)}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="RECOMMENDED">Empfohlen (Bilder zuerst)</MenuItem>
                                <MenuItem value="AZ">Name A–Z</MenuItem>
                                <MenuItem value="RANDOM">Zufällig</MenuItem>
                            </Select>

                            <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                                <Chip
                                    icon={<ShuffleRoundedIcon />}
                                    label="Neu mischen"
                                    clickable
                                    disabled={sortMode !== "RANDOM"}
                                    onClick={() => setShuffleSeed((s) => s + 1)}
                                    sx={{ fontWeight: 800 }}
                                />
                                <Chip
                                    label="Zurücksetzen"
                                    clickable
                                    onClick={() => {
                                        setQuery("");
                                        setOnlyWithImages(false);
                                        setSortMode("RECOMMENDED");
                                    }}
                                    sx={{ fontWeight: 800 }}
                                />
                            </Stack>

                            {activeChips.length > 0 && (
                                <>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                                        Aktiv
                                    </Typography>
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {activeChips.map((c) => (
                                            <Chip
                                                key={c.key}
                                                label={c.label}
                                                onDelete={c.onDelete}
                                                color="primary"
                                                variant="outlined"
                                                sx={{ fontWeight: 800 }}
                                            />
                                        ))}
                                    </Stack>
                                </>
                            )}
                        </Paper>

                        {/* Listing */}
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 4,
                                p: 2,
                                border: 0,
                                boxShadow: "none",
                                bgcolor: "background.paper",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                    {listingTitle}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary" }}>
                                    {filteredCompounds.length} Ergebnis{filteredCompounds.length === 1 ? "" : "se"}
                                </Typography>
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            <CompoundsListing compounds={filteredCompounds} title={""} />
                        </Paper>
                    </Box>
                </Box>
            </Container>
        </Main>
    );
}
